import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, EmptyState, Toggle } from '@playerbase/ui';

import { useShell } from '../../../shell/use-shell.js';
import {
  createPlaytest,
  fetchPlaytestsForGame,
} from '../../../games/playtests-data.js';
import { usePlaytestsRealtime } from '../../../games/use-playtests-realtime.js';
import { PlaytestsList } from './playtests-list.js';

import type { Playtest } from '@playerbase/types';

// Section index route for /g/:gameShortId/playtest-settings. Shows all
// non-archived playtests in one list (drafts and active mixed; the row
// badge distinguishes them) with a toggle to also include archived -
// mirroring the FormsSection pattern so the two listings feel the same.

export function PlaytestsSection(): JSX.Element {
  const { activeGame } = useShell();
  const { playtestsVersion, bumpPlaytestsVersion } = usePlaytestsRealtime();
  const navigate = useNavigate();
  const params = useParams<{ studioShortId: string; gameShortId: string }>();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [rows, setRows] = useState<Playtest[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setRows([]);
      return;
    }
    const next = await fetchPlaytestsForGame(activeGame.id);
    setRows(next);
  }, [activeGame]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime tick. Bumping the version is enough for the list; we don't
  // rebuild the supabase subscription per page (ShellProvider owns it).
  useEffect(() => {
    if (playtestsVersion === 0) return;
    void reload();
  }, [playtestsVersion, reload]);

  const activeRows = useMemo(
    () => rows.filter((p) => p.archivedAt === null),
    [rows],
  );
  const archivedCount = rows.length - activeRows.length;
  const visibleRows = showArchived ? rows : activeRows;

  async function handleCreate(): Promise<void> {
    if (activeGame === null) return;
    setCreating(true);
    setCreateError(null);
    const result = await createPlaytest(activeGame.id, 'Untitled playtest');
    setCreating(false);
    if (!result.ok || result.data === null) {
      setCreateError(
        result.error === 'not_a_member'
          ? "You don't have permission to create playtests in this studio."
          : 'Could not create the playtest. Please try again.',
      );
      return;
    }
    bumpPlaytestsVersion();
    if (params.studioShortId === undefined || params.gameShortId === undefined) return;
    navigate(
      `/s/${params.studioShortId}/g/${params.gameShortId}/playtest-settings/flows/${result.data.id}`,
    );
  }

  if (activeGame === null) {
    return <div className="text-sm text-text-muted">No game selected.</div>;
  }

  const isEmpty = activeRows.length === 0 && archivedCount === 0;

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold text-text">Playtest flows</h1>
        <p className="text-sm text-text-muted">
          Configure a sequence of in-game events and the questions to ask
          paired testers when each event fires.
        </p>
      </header>

      {createError !== null ? <Alert tone="danger">{createError}</Alert> : null}

      {isEmpty ? (
        <EmptyState
          title="No playtests yet"
          description="Start a new playtest to define the events you'd like to ask testers about."
          cta={
            <Button variant="primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating...' : 'New playtest'}
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            {archivedCount > 0 ? (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span id="show-archived-playtests-label">
                  Show archived ({archivedCount})
                </span>
                <Toggle
                  aria-label="Show archived playtests"
                  aria-labelledby="show-archived-playtests-label"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
              </div>
            ) : (
              <span />
            )}
            <Button variant="primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating...' : 'New playtest'}
            </Button>
          </div>
          <PlaytestsList rows={visibleRows} />
        </div>
      )}
      </div>
    </div>
  );
}
